import { create } from 'zustand';
import type { RenderMode } from '@/types/render';
import type {
  AssistantBlock,
  AssistantCardOutcome,
  AssistantRenderSettings,
} from '@/types/assistant';

/**
 * Client state of the AI architect (docs/assistant/ADR-001). The thread and its
 * stored messages are server state (TanStack Query, refetched when the panel
 * opens, after every turn and on app foreground); this store holds only what
 * exists nowhere else: the panel being open, the turn in flight, the input,
 * and the local Undo snapshots of settings patches.
 */

/** Thread scope: one thread per building, one general thread outside them. */
export const GENERAL_SCOPE = 'general';
export const scopeOf = (buildingId: string | null): string =>
  buildingId ?? GENERAL_SCOPE;

export interface AssistantTurn {
  scope: string;
  threadId: string;
  text: string;
  /** Last `status` event (`thinking`, `looking_up`); null once blocks arrive. */
  status: string | null;
  blocks: Array<AssistantBlock | { type: string }>;
  failed: boolean;
  /** The API's own words for a refused turn; null = connection lost. */
  error: string | null;
}

/**
 * Settings as they were before an `apply_render_settings`, for the message's
 * Undo. Keyed by `${messageId}:${blockIndex}`; a turn still streaming uses the
 * `turn` placeholder id until `done` names the stored message.
 */
export interface PatchUndo {
  designId: string;
  prior: AssistantRenderSettings;
  fields: string[];
  /** The tab before the patch switched it; null when the patch kept the tab. */
  priorMode: RenderMode | null;
  undone: boolean;
}

export const TURN_MESSAGE_ID = 'turn';
export const undoKey = (messageId: string, blockIndex: number): string =>
  `${messageId}:${blockIndex}`;

/** A patch waiting for its design's settings form to be on screen. */
export interface PendingPatch {
  designId: string;
  patch: Record<string, unknown>;
  undoKey: string;
}

interface AssistantState {
  isOpen: boolean;
  /** Text queued to send as soon as the panel has its thread (moment option). */
  queuedText: string | null;
  draft: string;
  turn: AssistantTurn | null;
  /** Patch fields this build skipped, reported with the next message. */
  skippedFields: string[];
  undo: Record<string, PatchUndo>;
  pendingPatch: PendingPatch | null;
  /**
   * What the user did with an action card on this device: "Not now" hides it,
   * the primary button marks it started so it cannot be pressed twice.
   */
  cardOutcomes: Record<string, AssistantCardOutcome>;

  open: (queuedText?: string) => void;
  close: () => void;
  /** Re-opens the panel after a page reload if it was open (see OPEN_KEY). */
  restoreOpen: () => void;
  takeQueuedText: () => string | null;
  setDraft: (draft: string) => void;
  startTurn: (scope: string, threadId: string, text: string) => void;
  setTurnStatus: (status: string) => void;
  addTurnBlock: (block: AssistantBlock | { type: string }) => void;
  failTurn: (error: string | null) => void;
  discardTurn: () => void;
  /** The stored messages now carry the turn; re-key its Undo snapshots. */
  finishTurn: (messageId: string | null) => void;
  addSkippedFields: (fields: string[]) => void;
  takeSkippedFields: () => string[];
  setUndo: (key: string, undo: PatchUndo) => void;
  markUndone: (key: string) => void;
  setPendingPatch: (pending: PendingPatch | null) => void;
  setCardOutcome: (key: string, outcome: AssistantCardOutcome) => void;
}

/**
 * Whether the panel is open survives a page reload (sessionStorage: this tab
 * only, so a fresh visit still starts closed). The store itself starts closed
 * so server and first client render agree; `restoreOpen` runs after mount.
 */
const OPEN_KEY = 'assistant-open';

function persistOpen(open: boolean) {
  try {
    if (open) sessionStorage.setItem(OPEN_KEY, '1');
    else sessionStorage.removeItem(OPEN_KEY);
  } catch {
    // Storage blocked (private mode): the panel just starts closed next load.
  }
}

export const useAssistantStore = create<AssistantState>((set, get) => ({
  isOpen: false,
  queuedText: null,
  draft: '',
  turn: null,
  skippedFields: [],
  undo: {},
  pendingPatch: null,
  cardOutcomes: {},

  open: (queuedText) => {
    persistOpen(true);
    set({ isOpen: true, ...(queuedText ? { queuedText } : {}) });
  },
  close: () => {
    persistOpen(false);
    set({ isOpen: false });
  },
  restoreOpen: () => {
    try {
      if (sessionStorage.getItem(OPEN_KEY) === '1') set({ isOpen: true });
    } catch {
      // Storage blocked: stay closed.
    }
  },
  takeQueuedText: () => {
    const text = get().queuedText;
    if (text !== null) set({ queuedText: null });
    return text;
  },
  setDraft: (draft) => set({ draft }),

  startTurn: (scope, threadId, text) =>
    set({
      turn: {
        scope,
        threadId,
        text,
        status: null,
        blocks: [],
        failed: false,
        error: null,
      },
    }),
  setTurnStatus: (status) =>
    set((state) =>
      state.turn ? { turn: { ...state.turn, status } } : state
    ),
  addTurnBlock: (block) =>
    set((state) =>
      state.turn
        ? {
            turn: {
              ...state.turn,
              status: null,
              blocks: [...state.turn.blocks, block],
            },
          }
        : state
    ),
  failTurn: (error) =>
    set((state) =>
      state.turn ? { turn: { ...state.turn, failed: true, error } } : state
    ),
  discardTurn: () => set({ turn: null }),
  finishTurn: (messageId) =>
    set((state) => {
      const prefix = `${TURN_MESSAGE_ID}:`;
      const undo: Record<string, PatchUndo> = {};
      const cardOutcomes: Record<string, AssistantCardOutcome> = {};
      for (const [key, value] of Object.entries(state.undo)) {
        if (!key.startsWith(prefix)) undo[key] = value;
        // A refused turn stores no message: its snapshots have nothing to
        // attach to and are dropped with it.
        else if (messageId) undo[messageId + key.slice(TURN_MESSAGE_ID.length)] = value;
      }
      for (const [key, outcome] of Object.entries(state.cardOutcomes)) {
        if (!key.startsWith(prefix)) cardOutcomes[key] = outcome;
        else if (messageId)
          cardOutcomes[messageId + key.slice(TURN_MESSAGE_ID.length)] = outcome;
      }
      const pendingPatch =
        state.pendingPatch?.undoKey.startsWith(prefix) && messageId
          ? {
              ...state.pendingPatch,
              undoKey:
                messageId +
                state.pendingPatch.undoKey.slice(TURN_MESSAGE_ID.length),
            }
          : state.pendingPatch;
      return { turn: null, undo, cardOutcomes, pendingPatch };
    }),

  addSkippedFields: (fields) =>
    set((state) => ({
      skippedFields: Array.from(new Set([...state.skippedFields, ...fields])),
    })),
  takeSkippedFields: () => {
    const fields = get().skippedFields;
    if (fields.length > 0) set({ skippedFields: [] });
    return fields;
  },
  setUndo: (key, undo) =>
    set((state) => ({ undo: { ...state.undo, [key]: undo } })),
  markUndone: (key) =>
    set((state) =>
      state.undo[key]
        ? { undo: { ...state.undo, [key]: { ...state.undo[key], undone: true } } }
        : state
    ),
  setPendingPatch: (pendingPatch) => set({ pendingPatch }),
  setCardOutcome: (key, outcome) =>
    set((state) => ({
      cardOutcomes: { ...state.cardOutcomes, [key]: outcome },
    })),
}));
